import { BodyBox, NotFoundPage, EntityForm } from '@lbcamden/lib-react';
import { DateTime } from 'luxon';
import { FunctionComponent, useMemo } from 'react';
import { useParams } from 'react-router';

import { PageBreadcrumbs } from '~/client/components/shared/PageBreadcrumbs';
import {
  useCurrentPeopleList,
  useCurrentUser,
  useProperty,
  useReferenceListDisplayValue,
  useReferenceListSelectOptions,
  useUserStore,
} from '~/client/hooks/storeHooks';
import { useGoBackOrNavigate } from '~/client/hooks/useGoBackOrNavigate';
import { getPropertyBreadcrumbs, toCasePage, toPropertyPage } from '~/client/utils/navigation';
import { tempNumericId } from '~/common/tinybase/utils';
import {
  CaseEventContactMethodCode,
  CaseEventInOrOutCode,
  NewCasePayload,
} from '~/common/types/cases';

import { useCreateCaseEvent } from '../useCreateCaseEvent';
import { newCaseFormConfig, newCaseFormSchema, NewCaseFormValues } from './config';

const title = 'New case';

export const CaseCreatePage: FunctionComponent = () => {
  const user = useCurrentUser();

  const { propertyId: propertyIdParam } = useParams();
  const propertyId = parseInt(propertyIdParam!);

  const navigate = useGoBackOrNavigate();

  const userStore = useUserStore();
  const refListDisplayValue = useReferenceListDisplayValue();
  const refListSelectOptions = useReferenceListSelectOptions();
  const property = useProperty(propertyId);
  const people = useCurrentPeopleList(propertyId);

  const tempCaseId = useMemo(tempNumericId, []);

  const formConfig = useMemo(
    () => newCaseFormConfig(people, refListSelectOptions),
    [people, refListSelectOptions],
  );

  const createCaseEvent = useCreateCaseEvent(tempCaseId);

  if (!property) {
    return <NotFoundPage />;
  }

  const goToPropertyPage = () => navigate(toPropertyPage(propertyId));

  const onSave = ({ details, ...caseValues }: NewCaseFormValues) => {
    const now = DateTime.now().toISO();

    // We show the user's name in the form but must supply their username when saving
    const leadOfficer = user?.camdenAccountName ?? '';
    const demand = refListDisplayValue('LWP_DEMAND', String(caseValues.demandCode));

    const payload: NewCasePayload = {
      ...caseValues,
      propertyId,
      leadOfficer,
      demand,
      caseId: tempCaseId,
      isVisitingProgramme: demand === 'Visiting Programme',
      createdBy: leadOfficer,
      isClosed: false,
      closeReason: '',
      reported: now,
      created: now,
      updated: '',
    };

    userStore?.store.transaction(() => {
      userStore?.handleAndDispatchMessage('createCase', propertyId, payload, {
        newEntityTempId: payload.caseId,
      });
      createCaseEvent(
        CaseEventContactMethodCode.GeneralInfo,
        CaseEventInOrOutCode.NotApplicable,
        details,
      );
    });

    void navigate(toCasePage(propertyId, payload.caseId));
  };

  return (
    <BodyBox>
      <PageBreadcrumbs
        css={{ marginBottom: 0 }}
        links={[...getPropertyBreadcrumbs(property), { name: title }]}
      />
      <EntityForm<NewCaseFormValues>
        title={title}
        schema={newCaseFormSchema}
        config={formConfig}
        initialValue={{ leadOfficer: user?.name }}
        onCancel={goToPropertyPage}
        onSave={onSave}
      />
    </BodyBox>
  );
};
